import { createVillageByPath } from "../api/geoAdminApi";
import type { GeoDirectoryStore } from "../data/geoDirectoryStore";
import type { TelanganaDistrictKey } from "../data/telanganaDistricts";
import { isTelanganaDistrictKey } from "../data/telanganaDistricts";
import { resolveMandalInDistrict, type UnmatchedLeadRow } from "./leadsImportGeoMatch";
import { normalizeGeoLabel } from "./geoLabel";

/** Village not on directory list under a resolved mandal. */
function isMissingVillageRow(u: UnmatchedLeadRow): boolean {
  return u.reason.includes("not found under mandal");
}

/** Mandal name from the file is not in that district’s directory (village was never checked). */
function isMissingMandalRow(u: UnmatchedLeadRow): boolean {
  return /^Mandal "/.test(u.reason) && u.reason.includes("not found in") && u.reason.includes("directory");
}

function isLeadGeoAutoCreateCandidate(u: UnmatchedLeadRow): boolean {
  return isMissingVillageRow(u) || isMissingMandalRow(u);
}

/** True when auto-create can add missing mandal and/or village under the row’s district. */
export function hasMissingVillageUnmatched(unmatched: UnmatchedLeadRow[]): boolean {
  return unmatched.some(isLeadGeoAutoCreateCandidate);
}

/**
 * POST missing mandals/villages for lead import: uses each row’s **District** (or batch PDF district)
 * and **file** mandal + village names. The API creates the mandal under that district if needed, then the village.
 */
export async function ensureMissingLeadVillages(
  store: GeoDirectoryStore,
  batchDistrictKey: TelanganaDistrictKey | null,
  unmatched: UnmatchedLeadRow[],
): Promise<{ created: number; existed: number; errors: string[] }> {
  const targets = unmatched.filter(isLeadGeoAutoCreateCandidate);
  const seen = new Set<string>();
  let created = 0;
  let existed = 0;
  const errors: string[] = [];

  for (const u of targets) {
    const dKey: TelanganaDistrictKey | null =
      u.fileDistrictKey != null && isTelanganaDistrictKey(u.fileDistrictKey)
        ? u.fileDistrictKey
        : batchDistrictKey && isTelanganaDistrictKey(batchDistrictKey)
          ? batchDistrictKey
          : null;
    if (!dKey) continue;

    const mandalFromFile = u.fileMandal.trim();
    const villageFromFile = u.fileVillage.trim();
    if (!mandalFromFile || !villageFromFile) continue;

    let mandalNameForApi = mandalFromFile;
    if (isMissingVillageRow(u) && !isMissingMandalRow(u)) {
      const resolved = resolveMandalInDistrict(store, dKey, u.fileMandal);
      if (!resolved) continue;
      mandalNameForApi = resolved;
    }

    const key = `${dKey}|${normalizeGeoLabel(mandalNameForApi)}|${normalizeGeoLabel(villageFromFile)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    try {
      const r = await createVillageByPath({
        districtKey: dKey,
        mandalName: mandalNameForApi,
        villageName: villageFromFile,
      });
      if (r.created) created += 1;
      else existed += 1;
    } catch (e) {
      errors.push(`${villageFromFile} (${mandalNameForApi}): ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  return { created, existed, errors };
}
